import type { Home, ResidentItem } from '@lmc/ts-common'
import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Select
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { FormInstance, RadioChangeEvent } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { getNominative } from 'common/utils'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useCarers from 'hooks/useFindItem/useCarers'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useRequiredRule from 'screens/Residents/screens/ResidentImportantPeopleScreens/forms/utils/useRequiredRule'
import { useUpdateKeyWorkers } from './hooks/useUpdateKeyworkers'

type CarersFormCardProps = {
  resident: ResidentItem
  form?: FormInstance<any>
  testId?: string
}

export const CarersFormCard = ({
  resident,
  form,
  testId
}: CarersFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const requiredRule = useRequiredRule()
  const [shouldShowCarers, setShouldShowCarers] = useState(!resident.assignAll)
  const { all: carers } = useCarers({})

  const flags = useFeatureFlag({
    flagNames: ['keyworkers']
  })?.data

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const flagsKeyworkersEnabled = flags?.keyworkers?.enabled

  const keyworkersEnabled =
    flagsKeyworkersEnabled && home?.features?.goals?.enabled

  const assignedCarers = useWatch('carers', form) || []

  const assignAll = useWatch('assignAll', form)

  const carerOptions = carers
    ?.filter(
      (carerPerson) =>
        carerPerson.name.first !== undefined &&
        carerPerson.name.last !== undefined &&
        carerPerson.active
    )
    .map((carer) => ({
      label:
        carer.name.first && carer.name.last
          ? `${carer.name.first} ${carer.name.last}`
          : t(supConstants.INACTIVE_CARER),
      value: carer.id
    }))

  const keyworkerOptions = carerOptions?.map((item) => ({
    ...item,
    disabled:
      assignAll === false ? !assignedCarers?.includes(item?.value) : false
  }))

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state?.global?.home?.preferredPeopleNominative || ''
  )

  const onRadioChange = (e: RadioChangeEvent) => {
    setShouldShowCarers(e.target.value === false)
  }

  const filterOption = (input: string, option?: { label: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  useUpdateKeyWorkers({ form, enabled: keyworkersEnabled })

  return (
    <FormCard title={t(supConstants.CARE_TEAM)} testId={testId}>
      <FormItem name="assignAll">
        <FormRadioGroup onChange={onRadioChange} value={resident.assignAll}>
          <FormRadio
            value
            text={t(supConstants.ASSIGN_TO_ALL_CARE_TEAM)}
            subText={t(supConstants.ASSIGN_TO_ALL_CARE_TEAM_SUBLABEL, {
              variable: getNominative(preferredPeopleNominative, 'Resident')
            })}
          />
          <FormRadio
            text={t(supConstants.ASSIGN_TO_SELECTED_CARE_TEAM_MEMBERS)}
            value={false}
          />
        </FormRadioGroup>
      </FormItem>
      {shouldShowCarers && (
        <FormItem
          label={<Text>{t(supConstants.ASSIGNED_CARE_TEAM)}</Text>}
          name="carers"
          rules={[requiredRule(t(supConstants.ASSIGNED_CARE_TEAM))]}
        >
          <Select
            mode="multiple"
            allowClear
            options={carerOptions}
            filterOption={filterOption}
          />
        </FormItem>
      )}
      {keyworkersEnabled && (
        <FormItem
          label={<Text>{t(supConstants.KEYWORKERS)}</Text>}
          name="keyworkers"
          extra={t(supConstants.KEYWORKERS_TOOLTIP_SELECT)}
          tooltip={t(supConstants.KEYWORKERS_TOOLTIP_HELP)}
        >
          <Select
            mode="multiple"
            allowClear
            options={keyworkerOptions}
            filterOption={filterOption}
          />
        </FormItem>
      )}
    </FormCard>
  )
}
